import type {
  AlbumOverrideProperties,
  AlbumOverridesMap,
} from "./album-overrides.types";

import localOverrides from "@/data/album-overrides/local.json";
import prdOverrides from "@/data/album-overrides/prd.json";

const env = import.meta.env.VITE_LOGICAL_ENV || "local";

// Keyed by VITE_LOGICAL_ENV. Overrides are looked up by album id, and album
// ids are per-database, so only prd needs a set of its own.
//
// Every non-prod build reports VITE_LOGICAL_ENV=dev or `preview` — `.env`,
// `.env.local` and `.env.dev` all set `dev` — so they share one set. These
// were mapped implicitly through the fallback below; they are explicit now so
// that adding an environment does not silently inherit another one's albums.
const overridesMap: Record<string, AlbumOverridesMap> = {
  local: localOverrides as AlbumOverridesMap,
  dev: localOverrides as AlbumOverridesMap,
  preview: localOverrides as AlbumOverridesMap,
  prd: prdOverrides as AlbumOverridesMap,
};

/**
 * Album overrides for the current environment.
 */
export const albumOverrides: AlbumOverridesMap =
  overridesMap[env] ?? (localOverrides as AlbumOverridesMap);

/**
 * Get override properties for a specific album.
 * Returns an empty object if no overrides exist.
 */
export function getAlbumOverrides(albumId: string): AlbumOverrideProperties {
  return albumOverrides[albumId] ?? {};
}

/**
 * Get the display name for an album's creator.
 * Returns the override artistLabel if defined, otherwise the provided default.
 */
export function getIsMasteredOverride(albumId: string): boolean | undefined {
  return getAlbumOverrides(albumId).isMastered;
}

export function getIsExclusive(albumId: string): boolean {
  return getAlbumOverrides(albumId).isExclusive ?? false;
}

export function getCreatorDisplayName(
  albumId: string,
  defaultName: string,
): string {
  const overrides = getAlbumOverrides(albumId);
  return overrides.artistLabel ?? defaultName;
}

/**
 * Get the overridden dollar interval between "Name Your Price" tiers for an
 * album. Returns undefined when no override exists (tiers are then computed
 * from the album's minimum price).
 */
export function getPriceIntervalOverride(albumId: string): number | undefined {
  return getAlbumOverrides(albumId).priceInterval;
}
